/**
 * The route guard's permission answer, pure, so it is tested against the real rule rather than a
 * copy. RouteRenderer in AppRoutes redirects to /unauthorized when this says denied.
 */
import {
  findSettingsPageForRoute,
  resolveSettingsRow,
} from '../components/platform/settings/settingsPermission'
import type {BackendRolePermissions} from '../utils/permissions'
import {hasModulePermission} from './hasModulePermission'
import type {RouteConfig} from './routeConfig'

/**
 * Whether the role's permissions keep it off a route that names a moduleKey. A route with no
 * moduleKey is never denied here, nor is any route but a Settings page's while no role is in the
 * store (the API still decides).
 */
export const isRouteDeniedByPermissions = (
  route: Pick<RouteConfig, 'id' | 'moduleKey' | 'submoduleKey' | 'strictSubmodule'>,
  backendPermissions: BackendRolePermissions | null | undefined,
): boolean => {
  if (!route.moduleKey) return false

  // STFFLG-4185: a route gated on a Settings page's row answers exactly as the Settings nav does,
  // from the one Settings resolver (an absent row takes the page's absentDefault, not the Platform
  // pass below, with or without a role in the store), so the menu never offers a page that
  // bounces and never hides one the URL still opens.
  const settingsPage = findSettingsPageForRoute(route)
  if (settingsPage) return !resolveSettingsRow(backendPermissions, settingsPage).canView

  if (!backendPermissions) return false

  const hasPermission = hasModulePermission(
    backendPermissions.modulePermissions,
    route.moduleKey,
    route.submoduleKey,
    {strict: route.strictSubmodule === true},
  )

  // An explicit deny blocks everyone, platform admins included - a
  // Platform-category role that unchecked a module must not reach it.
  // An absent node (undefined) carries no opinion: admin-category roles are
  // let through because not every route's node is seeded onto their role
  // (e.g. payroll > generate_report), while everyone else is denied.
  return hasPermission === false || (hasPermission === undefined && !backendPermissions.isPlatform)
}
